import { useEffect, useState } from 'react';

/**
 * Der Auftritt der Plattform selbst unter evercast.video. Verkaufsbotschaft (Rainers Vorgabe vom 19.09.2026,
 * dritte Fassung): Evercast macht aus einem einmal erstellten Webinar einen wiederholbaren Verkaufskanal.
 * Drei Nutzen: mehr Verkäufe, mehr Umsatz aus einem Webinar, weniger eigene Arbeitszeit. Sprache selbstbewusst,
 * keine Einkommensversprechen; Beispielrechnungen sind als solche gekennzeichnet (Konzept 5.1). Design bleibt.
 * Reihenfolge: Hero → wirtschaftlicher Nutzen → Zahlen → Umsatzrechner → Problem → drei Schritte → Produkt →
 * Funktionen → Live vs. Evercast → Weniger Technik → Zielgruppe → Preis → FAQ → Abschluss.
 */

/** Was im Preis enthalten ist – der tatsächliche Funktionsumfang der Plattform, nichts Geplantes. */
const PRICE_FEATURES: { title: string; items: string[] }[] = [
  {
    title: 'Webinare und Termine',
    items: [
      'Beliebig viele Webinare und Termine',
      'Feste Wochentage und Uhrzeiten oder der nächste runde Zeitpunkt',
      'Späteres Einsteigen erlaubt, Grenze einstellbar',
      'Abwesenheitszeiten ohne Termine',
    ],
  },
  {
    title: 'Anmeldung und Auftritt',
    items: [
      'Anmeldeseite mit Terminwahl unter deiner Adresse oder eigener Domain',
      'Dein Logo, deine Farben, hell oder dunkel, deine Schrift',
      'Botschutz ohne Captcha und ohne Double-Opt-in',
    ],
  },
  {
    title: 'Video und Webinar-Raum',
    items: [
      'Upload aus dem Browser, Aufbereitung in bis zu drei Qualitätsstufen',
      'Auslieferung von eigenen Servern in Deutschland',
      'Wartezimmer mit eigenem Bild',
      'Hinweise vom Team zur Minute',
      'Fragen der Teilnehmer, Antwort per Mail aus der Verwaltung',
      'Ein Zugang gilt für ein Gerät zur selben Zeit',
    ],
  },
  {
    title: 'Angebot',
    items: [
      'Angebot ab der Minute, die du festlegst',
      'Frist mit Countdown, wenn du eine echte hast',
      'Vollbild mit Angebots-Banner',
      'Aufzeichnung nach dem Termin, so lange du willst',
    ],
  },
  {
    title: 'E-Mails',
    items: [
      'Bestätigung mit Kalendereintrag',
      'Erinnerungen 24 Stunden, 1 Stunde und 10 Minuten vorher',
      'Absagen und Umbuchen per Link',
      'Nachfassmail, getrennt für Teilnehmer und Nicht-Erschienene',
      'Alle Texte anpassbar, je Projekt und je Webinar, mit Vorschau',
      'Eigene Absenderadresse',
    ],
  },
  {
    title: 'Zahlen und Kontakte',
    items: [
      'Anmeldungen, Show-Rate, Sehdauer',
      'Watch-Time-Kurve Minute für Minute mit Angebotsklicks',
      'CSV-Export für Excel',
      'Brevo-Anbindung: Kontakte mit Attributen in deiner Liste',
      'Newsletter-Häkchen mit Double-Opt-in',
    ],
  },
  {
    title: 'Betrieb und Datenschutz',
    items: [
      'Server in Deutschland, kein Tracking, kein Cookie-Banner',
      'Automatische Anonymisierung nach deiner Frist',
      'Tägliche Sicherung',
      'Persönlicher Support',
    ],
  },
];

/** Ziel des Knopfs "Mehr erfahren" im Kasten "Das kann Evercast" - die Erfolgsmagneten-Community. */
const COMMUNITY_URL = 'https://www.erfolgsmagneten.com';

export const TRIAL_PATH = '/kostenlos-testen';
export const LANDING_TITLE = 'Evercast – Einmal präsentieren. Immer wieder verkaufen.';

function TrialButton({ label = 'Kostenlos testen', large }: { label?: string; large?: boolean }) {
  return (
    <a className={`button${large ? ' button--large' : ''}`} href={TRIAL_PATH}>
      {label}
    </a>
  );
}

const NoCard = () => <p className="site__note">Keine Kreditkarte erforderlich.</p>;

export function LandingPage() {
  useEffect(() => {
    document.title = LANDING_TITLE;
    document.documentElement.setAttribute('data-scheme', 'light');
  }, []);

  return (
    <div className="site">
      <SiteHeader />

      {/* 1. Hero */}
      <section className="site__hero">
        <h1>
          Einmal präsentieren. <em>Immer wieder verkaufen.</em>
        </h1>
        <p className="site__sub">Mehr automatisierte Webinare. Mehr Verkäufe. Weniger Arbeit.</p>
        <p className="site__lead">
          Evercast macht aus deiner Webinar-Aufzeichnung einen automatisierten Verkaufsprozess. Interessenten melden sich an, erleben deine
          Präsentation und gelangen im richtigen Moment zu deinem Angebot.
        </p>
        <p className="site__claim">Du präsentierst einmal. Dein Webinar verkauft immer wieder.</p>
        <div className="site__actions">
          <TrialButton large />
          <a className="button button--large button--secondary" href="#produkt">
            Evercast ansehen
          </a>
        </div>
        <NoCard />
        <figure className="site__devices">
          <img className="site__desktop" src="/site/raum-desktop.png" alt="Der Webinar-Raum: Video, Hinweise vom Team, Fragen-Formular und das Angebot" />
          <img className="site__phone" src="/site/raum-phone.png" alt="Der Webinar-Raum auf dem Handy" />
        </figure>
      </section>

      {/* 2. Wirtschaftlicher Nutzen */}
      <section className="site__block">
        <div className="site__split">
          <div className="site__split-text">
            <h2>Ein Webinar. Immer wieder Umsatz.</h2>
            <div className="site__prose">
              <p>Du investierst einmal Zeit in deine Präsentation. Danach kann dasselbe Webinar immer wieder neue Teilnehmer erreichen und zu deinem Angebot führen.</p>
              <p className="site__stack">
                <span>Nicht einmal.</span>
                <span>Nicht nur am nächsten Dienstag um 18 Uhr.</span>
                <strong>Sondern immer wieder.</strong>
              </p>
            </div>
          </div>
          <img className="site__photo" src="/site/foto-coach.jpg" alt="Eine Beraterin sitzt entspannt mit Kaffee am Fenster, während ihr Webinar auf dem Laptop läuft" loading="lazy" />
        </div>
        <div className="site__benefit-grid site__benefit-grid--3">
          <Benefit icon="calendar" title="Mehr Webinare" text="Lass dein Webinar regelmäßig stattfinden, ohne jedes Mal selbst zu präsentieren." />
          <Benefit icon="target" title="Mehr Verkäufe" text="Führe Teilnehmer direkt aus deiner Präsentation zu deinem Angebot." />
          <Benefit icon="growth" title="Mehr Zeit fürs Business" text="Während Evercast die Präsentation übernimmt, arbeitest du an Kunden, Produkten, Marketing und Wachstum." />
        </div>
      </section>

      {/* 3. Zahlen: Webinare verkaufen */}
      <section className="site__block site__block--dark">
        <h2>Webinare sind kein Zeitvertreib. Sie verkaufen.</h2>
        <div className="site__numbers">
          <div>
            <strong>60 %</strong>
            <span>der Webinar-Anmeldungen werden im aktuellen ON24-Benchmark zu Teilnehmern.</span>
          </div>
          <div>
            <strong>49 Minuten</strong>
            <span>beschäftigen sich Teilnehmer durchschnittlich mit einem Webinar.</span>
          </div>
          <div>
            <strong>+49 %</strong>
            <span>mehr Interaktion mit Calls-to-Action pro Teilnehmer wurden 2025 gemessen.</span>
          </div>
          <div>
            <strong>+73 %</strong>
            <span>stiegen Demo-Buchungen im Webinar-Umfeld im Jahresvergleich.</span>
          </div>
        </div>
        <p className="site__source">Quelle: ON24 Digital Engagement Benchmarks Report 2026, Datenbasis 2025.</p>
      </section>

      {/* 4. Umsatzrechner */}
      <section id="rechner" className="site__block">
        <h2>Was kann dein automatisiertes Webinar für dein Business bringen?</h2>
        <p className="site__sub">Rechne selbst.</p>
        <Calculator />
      </section>

      {/* 5. Problem */}
      <section className="site__block site__block--tight">
        <div className="site__split site__split--reverse">
          <div className="site__split-text">
            <h2>Warum immer wieder deine Zeit für dasselbe Webinar opfern?</h2>
            <div className="site__prose">
              <p className="site__stack">
                <span>Du bereitest dich vor.</span>
                <span>Du blockierst deinen Kalender.</span>
                <span>Du hältst dieselbe Präsentation.</span>
                <span>Und beim nächsten Webinar beginnt alles wieder von vorne.</span>
              </p>
              <p>
                <strong>Damit ist Schluss.</strong>
              </p>
              <p>Mit Evercast präsentierst du einmal und setzt dasselbe Webinar immer wieder für deinen Verkauf ein.</p>
            </div>
          </div>
          <img className="site__photo" src="/site/foto-feierabend.jpg" alt="Ein Unternehmer geht am Abend entspannt durch die Stadt und sieht zufrieden auf sein Handy" loading="lazy" />
        </div>
        <div className="site__three">
          <div>
            <h3>Mehr Webinare ohne mehr Arbeit.</h3>
            <p>Dein Webinar läuft so oft, wie du es willst – du präsentierst es einmal.</p>
          </div>
          <div>
            <h3>Deine Zeit bleibt bei dir.</h3>
            <p>Kein blockierter Kalender, keine Wiederholung vor der Kamera.</p>
          </div>
          <div>
            <h3>Mehr Umsatz aus deiner Präsentation.</h3>
            <p>Jeder Termin führt neue Teilnehmer zu deinem Angebot.</p>
          </div>
        </div>
      </section>

      {/* 6. Drei Schritte */}
      <section id="ablauf" className="site__steps">
        <h2>Einmal einrichten. Immer wieder verkaufen.</h2>
        <ol className="site__step-list">
          <li>
            <span className="site__step-num">1</span>
            <h3>Webinar hochladen</h3>
            <p>Lade deine fertige Präsentation hoch und richte deinen Webinarraum ein.</p>
          </li>
          <li>
            <span className="site__step-num">2</span>
            <h3>Verkauf automatisieren</h3>
            <p>Lege Termine, Erinnerungen und den richtigen Zeitpunkt für dein Angebot fest.</p>
          </li>
          <li>
            <span className="site__step-num">3</span>
            <h3>Evercast arbeiten lassen</h3>
            <p>Teilnehmer melden sich an, erleben dein Webinar und gelangen direkt zu deinem Angebot.</p>
          </li>
        </ol>
        <p className="site__steps-claim">
          Du hältst das Webinar einmal.
          <br />
          Evercast übernimmt die Wiederholung.
        </p>
        <div className="site__actions">
          <TrialButton label="Webinar automatisieren" large />
        </div>
      </section>

      {/* 7. Produkt */}
      <section id="produkt" className="site__block">
        <h2>So verkauft dein Webinar mit Evercast.</h2>
        <div className="site__demo site__demo--4">
          <figure>
            <img src="/site/anmeldung.png" alt="Die Anmeldeseite mit Terminwahl" loading="lazy" />
            <figcaption>
              <h3>Aus Besuchern werden Teilnehmer.</h3>
              <p>Anmeldung mit Terminwahl – in 20 Sekunden.</p>
            </figcaption>
          </figure>
          <figure>
            <img src="/site/raum-desktop.png" alt="Der Webinar-Raum mit laufender Präsentation" loading="lazy" />
            <figcaption>
              <h3>Aus Teilnehmern werden Interessenten.</h3>
              <p>Deine Präsentation läuft wie live – ohne dich.</p>
            </figcaption>
          </figure>
          <figure>
            <img src="/site/raum-angebot.png" alt="Der Webinar-Raum mit eingeblendetem Angebot" loading="lazy" />
            <figcaption>
              <h3>Aus Interesse wird eine konkrete Handlung.</h3>
              <p>Dein Angebot erscheint zur Minute, die du bestimmst.</p>
            </figcaption>
          </figure>
          <figure>
            <img src="/site/zahlen.png" alt="Die Auswertung mit Watch-Time-Kurve" loading="lazy" />
            <figcaption>
              <h3>Aus Zahlen wird dein nächster Hebel.</h3>
              <p>Show-Rate, Sehdauer, Klicks – Minute für Minute.</p>
            </figcaption>
          </figure>
        </div>
        <div className="site__actions">
          <TrialButton label="Evercast ausprobieren" large />
        </div>
      </section>

      {/* 8. Funktionen in Ergebnissprache */}
      <section id="funktionen" className="site__block">
        <h2>Alles drin, was dein Webinar zum Verkaufen braucht.</h2>
        <div className="site__benefit-grid site__benefit-grid--3">
          <Benefit icon="calendar" title="Mehr Termine. Mehr Teilnehmer." text="Biete dein Webinar regelmäßig an, ohne dafür regelmäßig selbst vor der Kamera zu stehen." />
          <Benefit icon="bell" title="Mehr Teilnehmer erscheinen." text="Bestätigungen und Erinnerungen halten dein Webinar präsent." />
          <Benefit icon="target" title="Verkaufen, wenn das Interesse am höchsten ist." text="Blende dein Angebot genau in dem Moment ein, den du selbst bestimmst." />
          <Benefit icon="chart" title="Sieh, was verkauft." text="Erkenne, wie Teilnehmer dein Webinar nutzen und wo sie reagieren." />
          <Benefit icon="brush" title="Deine Marke. Dein Webinar." text="Vom Einstieg bis zum Angebot bleibt dein Auftritt konsistent." />
          <Benefit icon="question" title="Keine Frage geht verloren." text="Teilnehmer können Fragen stellen, ohne dass du während des Webinars live sein musst." />
        </div>
      </section>

      {/* 9. Live vs. Evercast */}
      <section className="site__block">
        <h2>Wie oft willst du für denselben Verkauf noch live gehen?</h2>
        <div className="site__versus">
          <div className="site__versus-col">
            <h3>Live-Webinar</h3>
            <ul>
              <li>Termin blockieren</li>
              <li>Präsentation erneut halten</li>
              <li>Eigene Zeit einsetzen</li>
              <li>Danach von vorne beginnen</li>
            </ul>
          </div>
          <div className="site__versus-col site__versus-col--us">
            <h3>Evercast</h3>
            <ul>
              <li>Einmal präsentieren</li>
              <li>Immer wieder ausspielen</li>
              <li>Automatisch zum Angebot führen</li>
              <li>Währenddessen am Business arbeiten</li>
            </ul>
          </div>
        </div>
        <p className="site__claim">Deine Präsentation skaliert. Deine Arbeitszeit nicht.</p>

        {/* Rechenbeispiel als Balken: ein Live-Termin pro Woche gegen Evercast jeden Abend – Arithmetik, keine Messwerte */}
        <div className="site__bars">
          <h3>Ein Monat im Vergleich</h3>
          <p className="site__bars-intro">Ein Live-Webinar pro Woche gegenüber Evercast jeden Abend um 19 Uhr.</p>
          <Bar label="Webinar-Termine im Monat" live="4" evercast="30" width={100} />
          <Bar label="Deine Zeit vor der Kamera" live="4 Stunden" evercast="0 Stunden" width={0} liveWidth={100} />
          <Bar label="Tage, an denen sich jemand anmelden und noch am selben Abend teilnehmen kann" live="4" evercast="30" width={100} />
          <Bar label="So oft sehen Teilnehmer dein Angebot" live="4×" evercast="30×" width={100} />
          <p className="site__bars-note">Rechenbeispiel: ein Live-Termin pro Woche mit 60 Minuten gegenüber einem Evercast-Termin täglich. Keine Messwerte.</p>
        </div>
      </section>

      {/* 10. Weniger Technik */}
      <section className="site__block site__block--dark">
        <h2>Verkaufen statt Webinarsoftware studieren.</h2>
        <div className="site__prose">
          <p>Evercast ist dafür gebaut, schnell einsatzbereit zu sein.</p>
        </div>
        <ol className="site__simple">
          <li>Webinar anlegen.</li>
          <li>Video hochladen.</li>
          <li>Termine festlegen.</li>
          <li>Angebot einbauen.</li>
          <li>Starten.</li>
        </ol>
        <p className="site__claim">Fertig.</p>
        <div className="site__prose">
          <p>Keine unnötige Technik. Kein kompliziertes System. Sondern ein Webinar, das immer wieder für dich verkauft.</p>
        </div>
      </section>

      {/* 11. Zielgruppe */}
      <section className="site__block site__block--soft">
        <div className="site__split">
          <img className="site__photo" src="/site/foto-praxis.jpg" alt="Eine Tierphysiotherapeutin behandelt in ihrer Praxis einen Golden Retriever" loading="lazy" />
          <div className="site__split-text">
            <h2>Für Unternehmer, die nicht jedes Mal selbst verkaufen wollen.</h2>
            <div className="site__prose">
              <p>Evercast ist für Coaches, Berater, Trainer, Agenturen, Experten und Unternehmen, die erklärungsbedürftige Angebote mit Webinaren verkaufen.</p>
              <p className="site__claim">
                Dein Wissen bleibt persönlich.
                <br />
                Deine Präsentation wird skalierbar.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 12. Preis – Zahl folgt, nichts erfunden */}
      <section id="preis" className="site__block">
        <h2>Was kostet es dich, immer wieder live zu gehen?</h2>
        <div className="site__price site__price--wide">
          <div className="site__price-head">
            <img className="site__logo site__logo--price" src="/site/logo.png" alt="Evercast" width={1185} height={239} loading="lazy" />
            <h3>Das kann Evercast</h3>
          </div>
          <div className="site__price-groups">
            {PRICE_FEATURES.map((group) => (
              <div key={group.title}>
                <h3>{group.title}</h3>
                <ul className="site__check site__check--tight">
                  {group.items.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="site__price-notice">
            <p>
              <strong>Evercast kann nicht gekauft werden.</strong> Evercast ist ein KI-Projekt der Erfolgsmagneten. Wir zeigen dir, wie einfach es
              heutzutage ist, deine eigene Broadcasting-Plattform zu bauen. Wenn auch du mehr dazu wissen willst, komm in die Erfolgsmagneten-Community.
            </p>
            <a className="button button--large" href={COMMUNITY_URL} rel="noopener">
              Mehr erfahren
            </a>
          </div>
        </div>
      </section>

      {/* 13. FAQ */}
      <section id="faq" className="site__faq">
        <h2>Häufige Fragen</h2>
        <Faq q="Muss ich mein Webinar mit Evercast aufnehmen?" a="Nein. Du nutzt deine vorhandene Aufzeichnung – als MP4 oder MOV." />
        <Faq q="Muss ich während des Webinars online sein?" a="Nein. Dein Webinar läuft zu den festgelegten Terminen von selbst und verkauft ohne dich." />
        <Faq q="Kann ich unterschiedliche Webinar-Termine anbieten?" a="Ja. Feste Wochentage und Uhrzeiten oder der nächste runde Zeitpunkt – deine Interessenten wählen." />
        <Faq q="Kann ich während des Webinars mein Angebot zeigen?" a="Ja. Dein Angebot erscheint ab der Minute, die du festlegst, und bleibt bis zum Ende sichtbar." />
        <Faq q="Brauche ich technische Vorkenntnisse?" a="Nein. Hochladen, Termine festlegen, Angebot einbauen, starten – dein erstes Webinar richten wir mit dir ein." />
        <Faq q="Kann ich Evercast kostenlos testen?" a="Ja. Ohne Kreditkarte. Du bekommst einen Testzugang und probierst alles mit deinem eigenen Webinar aus." />
      </section>

      {/* 14. Abschluss */}
      <section className="site__cta">
        <h2>Warum einmal verkaufen, wenn dein Webinar immer wieder verkaufen kann?</h2>
        <p className="site__cta-sub">Einmal präsentieren. Immer wieder verkaufen.</p>
        <p>Mach aus deiner besten Präsentation einen automatisierten Verkaufskanal.</p>
        <TrialButton label="Evercast kostenlos testen" large />
        <p className="site__cta-note">Keine Kreditkarte erforderlich.</p>
      </section>

      <SiteFooter />
    </div>
  );
}

/** Ein Balkenpaar im Rechenbeispiel: grau = live, farbig = Evercast; Breiten in Prozent des Maximums. */
function Bar({ label, live, evercast, width, liveWidth }: { label: string; live: string; evercast: string; width: number; liveWidth?: number }) {
  const liveW = liveWidth ?? Math.round((100 * 4) / 30);
  return (
    <div className="site__cbar">
      <div className="site__cbar-label">{label}</div>
      <div className="site__cbar-rows">
        <div className="site__cbar-row">
          <span className="site__cbar-fill site__cbar-fill--live" style={{ width: `${Math.max(liveW, 6)}%` }} />
          <span className="site__cbar-value">
            Live <strong>{live}</strong>
          </span>
        </div>
        <div className="site__cbar-row">
          <span className="site__cbar-fill site__cbar-fill--us" style={{ width: `${Math.max(width, 6)}%` }} />
          <span className="site__cbar-value">
            Evercast <strong>{evercast}</strong>
          </span>
        </div>
      </div>
    </div>
  );
}

/** Umsatzrechner: Der Besucher trägt seine Werte ein und sieht den Hebel eines wiederholten Webinars – als Beispielrechnung. */
function Calculator() {
  const [registrations, setRegistrations] = useState(200);
  const [attendance, setAttendance] = useState(60);
  const [salesRate, setSalesRate] = useState(5);
  const [revenuePerSale, setRevenuePerSale] = useState(1000);
  const [perMonth, setPerMonth] = useState(4);

  const participants = Math.round((registrations * attendance) / 100);
  const sales = Math.round((participants * salesRate) / 100 * 10) / 10;
  const revenue = Math.round(sales * revenuePerSale);
  const monthly = revenue * perMonth;
  const eur = (n: number) => `${n.toLocaleString('de-DE')} €`;
  const num = (v: string, fallback: number) => (v === '' ? 0 : Number.isFinite(Number(v)) ? Math.max(0, Number(v)) : fallback);

  return (
    <div className="site__calc">
      <div className="site__calc-inputs">
        <label>
          <span>Anmeldungen pro Webinar</span>
          <input type="number" min={0} value={registrations} onChange={(e) => setRegistrations(num(e.target.value, 200))} />
        </label>
        <label>
          <span>Teilnahmequote</span>
          <span className="site__calc-unit">
            <input type="number" min={0} max={100} value={attendance} onChange={(e) => setAttendance(Math.min(100, num(e.target.value, 60)))} />%
          </span>
        </label>
        <label>
          <span>Verkaufsquote der Teilnehmer</span>
          <span className="site__calc-unit">
            <input type="number" min={0} max={100} value={salesRate} onChange={(e) => setSalesRate(Math.min(100, num(e.target.value, 5)))} />%
          </span>
        </label>
        <label>
          <span>Durchschnittlicher Umsatz pro Verkauf</span>
          <span className="site__calc-unit">
            <input type="number" min={0} step={50} value={revenuePerSale} onChange={(e) => setRevenuePerSale(num(e.target.value, 1000))} />€
          </span>
        </label>
      </div>
      <div className="site__calc-result">
        <div className="site__calc-chain">
          <span>
            <strong>{participants.toLocaleString('de-DE')}</strong> Teilnehmer
          </span>
          <span aria-hidden="true">→</span>
          <span>
            <strong>{sales.toLocaleString('de-DE')}</strong> Verkäufe
          </span>
        </div>
        <div className="site__calc-big">{eur(revenue)} Umsatz</div>
        <p className="site__calc-sub">mit den oben gewählten Werten – pro Webinar-Termin.</p>
        <div className="site__calc-monthly">
          <label>
            <span>Und wenn dein Webinar</span>
            <input type="number" min={1} max={31} value={perMonth} onChange={(e) => setPerMonth(Math.max(1, Math.min(31, num(e.target.value, 4))))} />
            <span>Mal im Monat läuft?</span>
          </label>
          <div className="site__calc-month-result">
            <strong>{eur(monthly)}</strong> rechnerischer Monatsumsatz
          </div>
        </div>
      </div>
      <p className="site__calc-note">
        Beispielrechnung. Teilnahmequote, Verkaufsquote und Kundenwert können individuell stark variieren. Die Berechnung stellt keine Umsatz- oder
        Erfolgsgarantie dar. Die voreingestellte Teilnahmequote von 60 % basiert auf dem ON24 Webinar Benchmark 2026.
      </p>
    </div>
  );
}

/** Ziel aller „Kostenlos testen"-Knöpfe. Bis es eine Selbstregistrierung gibt, richten wir den Testzugang persönlich ein. */
export function TrialPage() {
  useEffect(() => {
    document.title = 'Kostenlos testen – Evercast';
    document.documentElement.setAttribute('data-scheme', 'light');
  }, []);
  return (
    <div className="site">
      <SiteHeader />
      <main className="site__trial">
        <h1>Evercast kostenlos testen</h1>
        <p className="site__lead">Du bekommst einen eigenen Zugang und probierst Evercast mit deinem eigenen Webinar aus – ohne Kreditkarte, ohne Laufzeit.</p>
        <ol className="site__trial-steps">
          <li>
            <strong>Du sagst uns kurz, wer du bist</strong>
            <span>und was du mit deinem Webinar verkaufst.</span>
          </li>
          <li>
            <strong>Wir richten deinen Zugang ein</strong>
            <span>und spielen deine Aufzeichnung ein, wenn du magst – meist am selben Werktag.</span>
          </li>
          <li>
            <strong>Du testest alles in Ruhe</strong>
            <span>Anmeldung, Webinarraum, Angebot, Mails, Auswertung.</span>
          </li>
        </ol>
        <a className="button button--large" href="mailto:info@erfolgsmagneten.com?subject=Evercast%20kostenlos%20testen&body=Hallo%2C%0A%0Aich%20m%C3%B6chte%20Evercast%20testen.%0A%0AMein%20Name%3A%20%0AMein%20Angebot%20%2F%20Thema%20des%20Webinars%3A%20%0A">
          Testzugang anfragen
        </a>
        <p className="site__note">Öffnet eine E-Mail an uns – Antwort meist am selben Werktag.</p>
      </main>
      <SiteFooter />
    </div>
  );
}

function SiteHeader() {
  return (
    <header className="site__bar">
      <a className="site__brand" href="/" aria-label="Evercast – zur Startseite">
        <img className="site__logo" src="/site/logo.png" alt="Evercast" width={1185} height={239} />
      </a>
      <nav className="site__nav">
        <a href="/#rechner">Umsatzrechner</a>
        <a href="/#ablauf">So funktioniert es</a>
        <a href="/#preis">Funktionen</a>
        <a href="/#faq">Fragen</a>
        <a className="button button--small" href="https://app.evercast.video/admin">
          Einloggen
        </a>
      </nav>
    </header>
  );
}

function SiteFooter() {
  return (
    <footer className="site__footer">
      <img className="site__logo site__logo--footer" src="/site/logo.png" alt="Evercast" width={1185} height={239} loading="lazy" />
      <div className="site__footer-links">
        <span>© {new Date().getFullYear()} Evercast · ein Projekt von Erfolgsmagneten</span>
        <a href="/impressum">Impressum</a>
        <a href="/datenschutz">Datenschutz</a>
      </div>
    </footer>
  );
}

type IconName = 'calendar' | 'target' | 'chart' | 'bell' | 'brush' | 'question' | 'growth';

function Benefit({ icon, title, text }: { icon: IconName; title: string; text: string }) {
  return (
    <div className="site__benefit">
      <span className="site__icon" aria-hidden="true">
        <Icon name={icon} />
      </span>
      <h3>{title}</h3>
      <p>{text}</p>
    </div>
  );
}

function Icon({ name }: { name: IconName }) {
  const common = { width: 26, height: 26, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.8, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };
  switch (name) {
    case 'calendar':
      return (
        <svg {...common}>
          <rect x="3" y="5" width="18" height="16" rx="2" />
          <path d="M3 10h18M8 3v4M16 3v4M9 15l2 2 4-4" />
        </svg>
      );
    case 'target':
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="9" />
          <circle cx="12" cy="12" r="5" />
          <circle cx="12" cy="12" r="1.5" fill="currentColor" />
        </svg>
      );
    case 'chart':
      return (
        <svg {...common}>
          <path d="M4 19V5M4 19h16M7 15l4-5 3 3 5-7" />
        </svg>
      );
    case 'growth':
      return (
        <svg {...common}>
          <path d="M4 18l6-6 4 4 6-8M15 8h5v5" />
        </svg>
      );
    case 'bell':
      return (
        <svg {...common}>
          <path d="M6 16V11a6 6 0 0112 0v5l2 2H4l2-2zM10 20a2 2 0 004 0" />
        </svg>
      );
    case 'brush':
      return (
        <svg {...common}>
          <path d="M14 4l6 6-8 8H6v-6l8-8zM12 6l6 6" />
        </svg>
      );
    case 'question':
      return (
        <svg {...common}>
          <path d="M4 5h16v11H9l-5 4V5z" />
          <path d="M10.5 9.5a1.8 1.8 0 113 1.2c-.8.5-1.2.9-1.2 1.8M12.3 14.6h.01" />
        </svg>
      );
  }
}

function Faq({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`site__faq-item${open ? ' is-open' : ''}`}>
      <button type="button" onClick={() => setOpen(!open)} aria-expanded={open}>
        {q}
        <span aria-hidden="true">{open ? '–' : '+'}</span>
      </button>
      {open && <p>{a}</p>}
    </div>
  );
}

/** Impressum und Datenschutz – Inhalt kommt vom Betreiber; bis dahin steht hier, was fehlt. */
/** Angaben von Rainer (19.09.2026). Adresse und Register nur hier ändern. */
function Impressum() {
  return (
    <>
      <h1>Impressum</h1>
      <p className="muted">Angaben gemäß § 5 DDG</p>
      <h2>Anbieter</h2>
      <p>
        Erfolgsmagneten GmbH
        <br />
        Leopoldstr. 244
        <br />
        80807 München
      </p>
      <p>
        Vertreten durch den Geschäftsführer: Rainer Stolte
        <br />
        Registergericht: Amtsgericht München · HRB 295540
      </p>
      <h2>Kontakt</h2>
      <p>
        Telefon: <a href="tel:+498924413422">+49 (0) 89 244 134 22</a>
        <br />
        E-Mail: <a href="mailto:info@erfolgsmagneten.com">info@erfolgsmagneten.com</a>
        <br />
        Web:{' '}
        <a href="https://www.erfolgsmagneten.com" rel="noopener">
          www.erfolgsmagneten.com
        </a>
      </p>
      <h2>Verantwortlich für den Inhalt nach § 18 Abs. 2 MStV</h2>
      <p>Rainer Stolte, Anschrift wie oben.</p>
      <h2>Umsatzsteuer</h2>
      <p>Umsatzsteuer-Identifikationsnummer gemäß § 27a UStG: DE370363054</p>
      <h2>Streitbeilegung</h2>
      <p>Wir sind nicht bereit und nicht verpflichtet, an Streitbeilegungsverfahren vor einer Verbraucherschlichtungsstelle teilzunehmen.</p>
    </>
  );
}

/**
 * Datenschutzerklärung für evercast.video, umgeschrieben aus der PraxiPet-Fassung (Rainer, 19.09.2026). Sie beschreibt,
 * was die Plattform tatsächlich tut: keine Cookies und kein Tracking auf der öffentlichen Seite, ein Sitzungs-Cookie
 * nur in der Verwaltung, Anschau-Erfassung im Webinar-Raum, Mails über Brevo, Hosting und Sicherung bei IONOS,
 * Anonymisierung nach der Aufbewahrungsfrist des Projekts. Wer den Code ändert, prüft, ob dieser Text noch stimmt.
 */
function Datenschutz() {
  const controller = (
    <p>
      Erfolgsmagneten GmbH
      <br />
      Leopoldstr. 244
      <br />
      80807 München
      <br />
      Deutschland
    </p>
  );
  return (
    <>
      <h1>Datenschutzerklärung</h1>
      <p className="muted">Stand: 19. September 2026</p>
      <p>
        Der Schutz personenbezogener Daten ist uns wichtig. Nachfolgend informieren wir darüber, welche personenbezogenen Daten beim Besuch unserer Website
        evercast.video, bei der Kontaktaufnahme mit uns und bei der Nutzung von Evercast verarbeitet werden.
      </p>
      <p>
        Dabei unterscheiden wir zwischen der öffentlich zugänglichen Evercast-Website und der eigentlichen Evercast-Plattform, mit der unsere Kunden
        automatisierte Webinare veranstalten. Weder auf der Website noch in der Plattform setzen wir Analyse- oder Marketingtechnologien ein. Es gibt kein
        Werbetracking, keine Cookies für Werbezwecke und kein Cookie-Banner, weil es nichts gibt, in das eingewilligt werden müsste. Teilnehmer-, Webinar-
        und Kundendaten werden weder für Werbung oder Marketingprofile noch für das Training von KI-Modellen verwendet.
      </p>

      <h2>1. Verantwortlicher</h2>
      <p>Verantwortlicher im Sinne der Datenschutz-Grundverordnung (DSGVO) für die Website evercast.video und die eigenen Datenverarbeitungen im Zusammenhang mit Evercast ist:</p>
      {controller}
      <p>
        Telefon: <a href="tel:+498924413422">+49 (0) 89 244 134 22</a>
        <br />
        E-Mail: <a href="mailto:info@erfolgsmagneten.com">info@erfolgsmagneten.com</a>
        <br />
        Web:{' '}
        <a href="https://www.erfolgsmagneten.com" rel="noopener">
          www.erfolgsmagneten.com
        </a>
      </p>
      <p>
        Registergericht: Amtsgericht München · Handelsregisternummer: HRB 295540
        <br />
        Geschäftsführer: Rainer Stolte
        <br />
        Umsatzsteuer-Identifikationsnummer: DE370363054
      </p>
      <p>Evercast ist ein Projekt der Erfolgsmagneten GmbH.</p>

      <h2>2. Unterschied zwischen Evercast-Website und Evercast-Plattform</h2>
      <p>Für den Datenschutz ist zwischen zwei Bereichen zu unterscheiden.</p>
      <h3>Öffentliche Website</h3>
      <p>
        Die Website evercast.video dient der Information über Evercast, der Vorstellung der Funktionen sowie der Gewinnung und Betreuung von Interessenten und
        Kunden. Für die Verarbeitung personenbezogener Daten auf dieser Website ist die Erfolgsmagneten GmbH verantwortlich. Auf der Website werden keine
        Cookies gesetzt und keine Analyse- oder Marketingdienste eingesetzt.
      </p>
      <h3>Evercast-Plattform</h3>
      <p>
        Evercast wird von Unternehmen, Coaches, Beratern, Praxen und anderen Veranstaltern genutzt, um ein aufgezeichnetes Webinar zu festen Terminen zu
        zeigen, Anmeldungen entgegenzunehmen, Erinnerungen zu versenden, Fragen zu sammeln und ein Angebot einzublenden. Jeder Veranstalter erhält dafür einen
        eigenen Bereich unter einer eigenen Adresse (zum Beispiel name.evercast.video oder eine eigene Domain).
      </p>
      <p>
        Soweit ein Veranstalter personenbezogene Daten seiner Webinar-Teilnehmer, Interessenten oder Kunden innerhalb von Evercast verarbeitet, entscheidet der
        Veranstalter über Zweck und Mittel dieser Verarbeitung und ist damit datenschutzrechtlich Verantwortlicher. Die Erfolgsmagneten GmbH verarbeitet diese
        Daten im Rahmen der Bereitstellung von Evercast im Auftrag des jeweiligen Veranstalters gemäß Art. 28 DSGVO.
      </p>
      <p>
        Wenn Sie sich also als Teilnehmer für ein Webinar anmelden, das ein Veranstalter über Evercast anbietet, ist für diese Datenverarbeitung in erster Linie
        der betreffende Veranstalter verantwortlich. Informationen über die Rechtsgrundlage und die konkrete Verwendung Ihrer Daten erhalten Sie von diesem
        Veranstalter. Sein Name steht auf der Anmeldeseite und in jeder E-Mail, die Sie über Evercast erhalten.
      </p>

      <h2>3. Hosting und Datensicherung</h2>
      <p>Unsere Website sowie die technische Infrastruktur von Evercast werden bei der</p>
      <p>
        IONOS SE
        <br />
        Elgendorfer Straße 57
        <br />
        56410 Montabaur
        <br />
        Deutschland
      </p>
      <p>
        auf Servern in Deutschland betrieben. IONOS stellt uns Server-, Hosting- und Speicherleistungen zur Verfügung. Auch die Videos der Veranstalter werden
        von unseren eigenen Servern bei IONOS ausgeliefert; ein externes Content Delivery Network wird nicht eingesetzt. Tägliche Sicherungskopien der Datenbank
        und der Videodateien liegen verschlüsselt übertragen in einem Objektspeicher von IONOS in einem Rechenzentrum in Deutschland und werden nach 30 Tagen
        automatisch gelöscht.
      </p>
      <p>
        Beim Aufruf unserer Website und der Plattform werden technisch erforderliche Informationen verarbeitet. Hierzu gehören die IP-Adresse des aufrufenden
        Geräts, Datum und Uhrzeit des Zugriffs, die aufgerufene Adresse, Browsertyp und Browserversion sowie technische Statusinformationen. Diese Angaben
        stehen in Server-Protokollen, die fortlaufend überschrieben werden und in der Regel nur wenige Tage vorhanden sind.
      </p>
      <p>
        Die Verarbeitung ist erforderlich, um Website und Plattform technisch bereitzustellen, einen sicheren Betrieb zu gewährleisten, Fehler zu erkennen und
        Angriffe oder missbräuchliche Zugriffe abzuwehren. Zur Abwehr automatisierter Anmeldungen zählen wir Anfragen je IP-Adresse für die Dauer einer Stunde
        im Arbeitsspeicher; eine dauerhafte Speicherung der IP-Adresse zu einer Anmeldung erfolgt nicht.
      </p>
      <p>
        Rechtsgrundlage ist Art. 6 Abs. 1 lit. f DSGVO. Unser berechtigtes Interesse liegt im sicheren, stabilen und zuverlässigen Betrieb unserer Website und
        unserer technischen Systeme. Soweit IONOS personenbezogene Daten in unserem Auftrag verarbeitet, erfolgt dies auf Grundlage von Art. 28 DSGVO.
      </p>

      <h2>4. TLS-Verschlüsselung</h2>
      <p>
        Unsere Website und die Evercast-Plattform werden ausschließlich verschlüsselt übertragen (TLS). Dadurch werden Daten, die zwischen Ihrem Browser und
        unseren Servern übertragen werden, während der Übertragung vor dem Zugriff unbefugter Dritter geschützt. Eine verschlüsselte Verbindung erkennen Sie
        daran, dass die Adresse im Browser mit „https://" beginnt.
      </p>

      <h2>5. Cookies und Speicherung im Browser</h2>
      <p>
        Auf der öffentlichen Website evercast.video und auf den Anmelde- und Webinar-Seiten der Veranstalter setzen wir keine Cookies und speichern nichts im
        Browser. Ein Cookie-Banner ist deshalb nicht erforderlich.
      </p>
      <p>
        Nur im geschützten Verwaltungsbereich für Veranstalter setzen wir ein technisch erforderliches Sitzungs-Cookie, das den angemeldeten Benutzer für die
        Dauer der Sitzung erkennt, sowie eine lokale Speicherung von Oberflächeneinstellungen (zum Beispiel, welche Bereiche auf- oder zugeklappt sind). Der
        Zugriff auf das Endgerät erfolgt auf Grundlage von § 25 Abs. 2 TDDDG, die damit verbundene Verarbeitung personenbezogener Daten auf Grundlage von Art. 6
        Abs. 1 lit. b DSGVO.
      </p>
      <p>Statistik-, Reichweiten- oder Marketingtechnologien setzen wir nicht ein.</p>

      <h2>6. Kontaktaufnahme und Testzugang</h2>
      <p>
        Wenn Sie uns per E-Mail oder telefonisch kontaktieren, etwa um Evercast kostenlos zu testen, verarbeiten wir die von Ihnen übermittelten
        personenbezogenen Daten zur Bearbeitung Ihres Anliegens. Hierzu können Ihr Name, Ihre E-Mail-Adresse, Telefonnummer, Ihr Unternehmens- oder
        Praxisname sowie der Inhalt Ihrer Nachricht gehören.
      </p>
      <p>
        Wenn Ihre Kontaktaufnahme auf den Abschluss oder die Durchführung eines Vertrages gerichtet ist, erfolgt die Verarbeitung auf Grundlage von Art. 6 Abs. 1
        lit. b DSGVO. Bei sonstigen Anfragen erfolgt sie auf Grundlage von Art. 6 Abs. 1 lit. f DSGVO; unser berechtigtes Interesse besteht in der Bearbeitung
        und Beantwortung der an uns gerichteten Kommunikation.
      </p>

      <h2>7. Kundenkonten der Veranstalter</h2>
      <p>
        Für die Nutzung von Evercast richten wir für jeden Veranstalter ein Projekt mit Zugang zur Verwaltung ein. Dabei verarbeiten wir Name, E-Mail-Adresse
        und Passwort (nur als kryptografischer Hash gespeichert) der Benutzer, den Namen des Projekts, die Absenderadresse für E-Mails, Logo und
        Erscheinungsbild sowie die vom Veranstalter hochgeladenen Videos, Texte und Angebote. Fehlgeschlagene Anmeldeversuche an der Verwaltung werden mit
        IP-Adresse und teilweise verdeckter E-Mail-Adresse protokolliert, um Angriffe zu erkennen.
      </p>
      <p>
        Diese Daten benötigen wir zur Einrichtung und Verwaltung des Zugangs, zur Erbringung unserer vertraglichen Leistungen, zur Kundenkommunikation sowie zur
        Abrechnung. Rechtsgrundlage ist Art. 6 Abs. 1 lit. b DSGVO. Soweit wir Informationen aufgrund handels- oder steuerrechtlicher Vorgaben aufbewahren
        müssen, erfolgt die Verarbeitung auf Grundlage von Art. 6 Abs. 1 lit. c DSGVO.
      </p>

      <h2>8. Anmeldung und Teilnahme an Webinaren</h2>
      <p>Wenn Sie sich als Teilnehmer für ein Webinar anmelden, das ein Veranstalter über Evercast anbietet, werden folgende Daten verarbeitet:</p>
      <ul>
        <li>Ihre E-Mail-Adresse, Ihr Vorname (freiwillig) und der gewählte Termin;</li>
        <li>ein persönlicher Zugangslink, mit dem Sie den Webinar-Raum betreten, absagen oder umbuchen können;</li>
        <li>Herkunftsangaben aus dem Link, über den Sie zur Anmeldeseite kamen (UTM-Parameter), sofern der Veranstalter solche Links verwendet;</li>
        <li>
          während des Webinars alle zehn Sekunden die Position im Video, damit der Veranstalter sieht, wie lange das Webinar angesehen wurde, sowie eine
          zufällige Kennung des geöffneten Browserfensters, damit ein Zugang nur auf einem Gerät zur selben Zeit genutzt wird. Diese Kennung wird nicht im
          Browser gespeichert und bei jedem Öffnen neu erzeugt;
        </li>
        <li>Fragen, die Sie im Webinar-Raum stellen, mit der Videominute, zu der Sie gestellt wurden, und die Antwort des Veranstalters;</li>
        <li>ob und wann Sie auf das eingeblendete Angebot geklickt haben.</li>
      </ul>
      <p>
        Für diese Verarbeitung ist der Veranstalter verantwortlich; sie dient der Durchführung des Webinars, das Sie angefordert haben (Art. 6 Abs. 1 lit. b
        DSGVO) und der Auswertung des Webinars durch den Veranstalter (Art. 6 Abs. 1 lit. f DSGVO). Eine Bestätigung der Anmeldung per Double-Opt-in findet
        nicht statt, weil die Anmeldung selbst kein Werbeversand ist.
      </p>
      <p>
        Ein Veranstalter kann auf der Anmeldeseite zusätzlich ein Häkchen für seinen Newsletter anbieten. Dieses Häkchen ist freiwillig und getrennt von der
        Anmeldung. Setzen Sie es, erhalten Sie eine E-Mail mit einem Bestätigungslink; erst mit dem Klick darauf gilt die Einwilligung als erteilt (Art. 6
        Abs. 1 lit. a DSGVO). Zeitpunkt von Anforderung und Bestätigung werden gespeichert. Die Einwilligung können Sie jederzeit über den Abmeldelink im
        Newsletter oder beim Veranstalter widerrufen.
      </p>

      <h2>9. E-Mail-Versand über Brevo</h2>
      <p>
        Für den Versand der E-Mails aus Evercast (Anmeldebestätigung mit Kalendereintrag, Erinnerungen, Antworten auf Fragen, Nachricht nach dem Webinar,
        Bestätigung des Newsletter-Häkchens) und für die Kontaktlisten der Veranstalter setzen wir die
      </p>
      <p>
        Brevo (Sendinblue GmbH)
        <br />
        Köpenicker Straße 126
        <br />
        10179 Berlin
        <br />
        Deutschland
      </p>
      <p>
        als Dienstleister ein. An Brevo werden die für die Zustellung erforderlichen Daten übermittelt, insbesondere E-Mail-Adresse, Vorname, Betreff und Inhalt
        der Nachricht. Veranstalter können außerdem festlegen, dass ihre Anmeldungen als Kontakte in eine Liste ihres eigenen Brevo-Kontos übernommen werden,
        mit Angaben zu Webinar, Termin, Teilnahme, Angebotsklick und Newsletter-Einwilligung. Über Zweck, Empfänger und Inhalt dieser Kommunikation entscheidet
        der jeweilige Veranstalter. Brevo ist vertraglich nach Art. 28 DSGVO gebunden und verarbeitet die Daten in der Europäischen Union.
      </p>

      <h2>10. Kein Werbetracking, keine KI-Nutzung</h2>
      <p>
        Weder auf evercast.video noch auf den Seiten der Veranstalter setzen wir Werbetracking ein, insbesondere kein Meta-Pixel und keine Webanalyse. Teilnehmer-,
        Webinar-, Kunden- und Videodaten werden durch die Erfolgsmagneten GmbH nicht dazu verwendet, Werbeprofile zu erstellen, personalisierte Werbung
        auszuspielen oder Marketingzielgruppen aufzubauen. Diese Daten werden auch nicht zum Training eigener oder fremder KI-Modelle verwendet; Evercast
        enthält keine KI-Bausteine.
      </p>

      <h2>11. Empfänger und Dienstleister</h2>
      <p>
        Wir geben personenbezogene Daten nur weiter, wenn hierfür eine Rechtsgrundlage besteht oder dies zur Erbringung unserer Leistungen erforderlich ist.
        Empfänger können Hosting- und IT-Dienstleister (IONOS SE), der Dienstleister für den E-Mail-Versand (Brevo / Sendinblue GmbH), Steuerberater,
        Zahlungs- bzw. Abrechnungsdienstleister nach deren zukünftiger Einbindung sowie Behörden oder sonstige öffentliche Stellen sein, soweit wir gesetzlich
        zu einer Übermittlung verpflichtet sind. Dienstleister, die Daten ausschließlich in unserem Auftrag verarbeiten, werden entsprechend Art. 28 DSGVO
        eingebunden.
      </p>

      <h2>12. Datenübermittlungen außerhalb der EU und des EWR</h2>
      <p>
        Alle von uns eingesetzten Dienstleister verarbeiten die Daten in Deutschland beziehungsweise in der Europäischen Union. Eine Übermittlung
        personenbezogener Daten in Drittländer findet durch uns nicht statt.
      </p>

      <h2>13. Speicherdauer und Löschkonzept</h2>
      <p>Wir speichern personenbezogene Daten nur so lange, wie dies für den jeweiligen Verarbeitungszweck erforderlich ist.</p>
      <p>
        Anmeldungen zu Webinaren werden nach Ablauf einer vom Veranstalter festgelegten Frist nach dem Termin automatisch anonymisiert; voreingestellt ist ein
        Jahr. Dabei werden E-Mail-Adresse, Name, Zugangslink, Herkunftsangaben und Newsletter-Links entfernt und die Aufzeichnungen der Anschauzeit sowie die
        gestellten Fragen gelöscht. Erhalten bleiben allein Zahlen ohne Personenbezug, etwa wie lange das Webinar angesehen wurde, damit die Statistik des
        Veranstalters stimmt.
      </p>
      <p>
        Daten aus allgemeinen Kontaktanfragen werden gelöscht, sobald die Anfrage abschließend bearbeitet wurde und keine gesetzlichen, vertraglichen oder
        berechtigten Gründe für eine weitere Speicherung bestehen. Vertrags- und Abrechnungsdaten werden entsprechend den handels- und steuerrechtlichen
        Aufbewahrungspflichten gespeichert. Sicherungskopien werden nach 30 Tagen überschrieben.
      </p>

      <h2>14. Widerruf einer Einwilligung</h2>
      <p>
        Wenn eine Datenverarbeitung auf Ihrer Einwilligung beruht (bei Evercast betrifft das allein das Newsletter-Häkchen eines Veranstalters), können Sie diese
        Einwilligung jederzeit mit Wirkung für die Zukunft widerrufen. Die Rechtmäßigkeit der bis zum Widerruf erfolgten Verarbeitung wird dadurch nicht berührt.
      </p>

      <h2>15. Widerspruch gegen Verarbeitung aufgrund berechtigter Interessen</h2>
      <p>
        Soweit wir personenbezogene Daten auf Grundlage von Art. 6 Abs. 1 lit. f DSGVO verarbeiten, haben Sie nach Art. 21 DSGVO das Recht, aus Gründen, die
        sich aus Ihrer besonderen Situation ergeben, jederzeit Widerspruch gegen diese Verarbeitung einzulegen. Wir verarbeiten die betreffenden Daten
        anschließend nicht mehr, es sei denn, wir können zwingende schutzwürdige Gründe für die Verarbeitung nachweisen, die Ihre Interessen, Rechte und
        Freiheiten überwiegen, oder die Verarbeitung dient der Geltendmachung, Ausübung oder Verteidigung von Rechtsansprüchen. Gegen die Verarbeitung
        personenbezogener Daten zum Zweck der Direktwerbung können Sie jederzeit widersprechen.
      </p>

      <h2>16. Ihre Datenschutzrechte</h2>
      <p>
        Nach Maßgabe der gesetzlichen Voraussetzungen haben Sie das Recht auf Auskunft gemäß Art. 15 DSGVO, Berichtigung gemäß Art. 16 DSGVO, Löschung gemäß
        Art. 17 DSGVO, Einschränkung der Verarbeitung gemäß Art. 18 DSGVO sowie Datenübertragbarkeit gemäß Art. 20 DSGVO. Darüber hinaus können Sie erteilte
        Einwilligungen jederzeit für die Zukunft widerrufen und unter den Voraussetzungen des Art. 21 DSGVO Widerspruch gegen bestimmte Verarbeitungen
        einlegen.
      </p>
      <p>Zur Wahrnehmung Ihrer Rechte können Sie sich an uns wenden:</p>
      {controller}
      <p>
        E-Mail: <a href="mailto:info@erfolgsmagneten.com">info@erfolgsmagneten.com</a>
      </p>
      <p>
        Soweit es um personenbezogene Daten geht, die ein Veranstalter im Zusammenhang mit seinen Webinaren über Evercast verarbeitet, wenden Sie sich bitte
        zunächst an den betreffenden Veranstalter. Dieser ist für die jeweilige Verarbeitung datenschutzrechtlich verantwortlich. Wir unterstützen ihn dabei,
        Ihre Anfrage zu beantworten.
      </p>

      <h2>17. Beschwerderecht bei einer Aufsichtsbehörde</h2>
      <p>
        Sie haben gemäß Art. 77 DSGVO das Recht, sich bei einer Datenschutzaufsichtsbehörde zu beschweren, wenn Sie der Auffassung sind, dass personenbezogene
        Daten rechtswidrig verarbeitet werden. Für die Erfolgsmagneten GmbH ist zuständig:
      </p>
      <p>
        Bayerisches Landesamt für Datenschutzaufsicht (BayLDA)
        <br />
        Promenade 18
        <br />
        91522 Ansbach
        <br />
        Deutschland
      </p>

      <h2>18. Automatisierte Entscheidungen</h2>
      <p>
        Wir setzen keine ausschließlich auf einer automatisierten Verarbeitung beruhenden Entscheidungen im Sinne von Art. 22 DSGVO ein, die Ihnen gegenüber
        rechtliche Wirkung entfalten oder Sie in vergleichbarer Weise erheblich beeinträchtigen.
      </p>

      <h2>19. Sicherheit der Verarbeitung</h2>
      <p>
        Wir treffen angemessene technische und organisatorische Maßnahmen, um personenbezogene Daten vor Verlust, Manipulation, unberechtigter Offenlegung und
        unberechtigtem Zugriff zu schützen. Dazu gehören unter anderem die verschlüsselte Übertragung, die strikte Trennung der Daten verschiedener Veranstalter
        in der Datenbank, gehashte Passwörter, begrenzte Anmeldeversuche, tägliche Sicherungen und regelmäßige Sicherheitsaktualisierungen der Server. Unsere
        Sicherheitsmaßnahmen werden entsprechend der technischen Entwicklung und unter Berücksichtigung des jeweiligen Risikos überprüft und weiterentwickelt.
      </p>

      <h2>20. Änderungen dieser Datenschutzerklärung</h2>
      <p>
        Wir können diese Datenschutzerklärung anpassen, wenn sich Evercast, unsere Website, eingesetzte Dienstleister oder die rechtlichen Rahmenbedingungen
        ändern. Es gilt die jeweils auf evercast.video veröffentlichte aktuelle Fassung.
      </p>
      <p className="muted">
        Stand: 19. September 2026
        <br />
        Evercast – ein Projekt der Erfolgsmagneten GmbH · Leopoldstr. 244 · 80807 München · Deutschland
        <br />
        Telefon: +49 (0) 89 244 134 22 · E-Mail: info@erfolgsmagneten.com
      </p>
    </>
  );
}

export function LegalPage({ kind }: { kind: 'impressum' | 'datenschutz' }) {
  useEffect(() => {
    document.title = `${kind === 'impressum' ? 'Impressum' : 'Datenschutz'} – Evercast`;
  }, [kind]);
  return (
    <div className="site">
      <SiteHeader />
      <main className="site__legal">{kind === 'impressum' ? <Impressum /> : <Datenschutz />}</main>
      <SiteFooter />
    </div>
  );
}
